import { Navbar } from '../components/Navbar';
import SEO from '../components/SEO';
import { Shield, FileText, Trash2, Lock, Clock, AlertCircle, Check, Database, Eye, Scale } from 'lucide-react';

export default function DriverDocumentPolicyPage() {
  return (
    <>
      <SEO 
        title="Driver Document Handling Policy - Hambre"
        description="Learn how Hambre handles, stores, and protects your driver application documents in compliance with GDPR."
      />
      <div>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-20 pb-12">
          <div className="max-w-3xl mx-auto px-4">
            {/* Header */}
            <div className="text-center mb-10">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Driver Document Handling Policy</h1>
              <p className="text-slate-500">
                How we handle, store, and protect your documents during the verification process
              </p>
              <p className="text-sm text-slate-400 mt-2">Last updated: February 2026</p>
            </div>

            <div className="space-y-8">
              {/* Key Points Summary */}
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6" data-testid="key-points">
                <h2 className="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Key Points
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Trash2 className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-green-800">Documents Deleted After Review</p>
                      <p className="text-sm text-green-700">We do not keep copies of your original documents</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Database className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-green-800">Minimal Data Storage</p>
                      <p className="text-sm text-green-700">Only essential metadata is retained</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Lock className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-green-800">GDPR Compliant</p>
                      <p className="text-sm text-green-700">Fully compliant with UK/EU data protection regulations</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Eye className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-green-800">Limited Access</p>
                      <p className="text-sm text-green-700">Only authorised staff can view during review</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Legal Basis Section - NEW */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Scale className="w-4 h-4 text-purple-600" />
                  </div>
                  Legal Basis for Processing
                </h2>
                <p className="text-slate-600 mb-4">
                  Hambre processes driver documentation under the following lawful bases pursuant to Article 6 of the UK GDPR / EU GDPR:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-800">Legal Obligation (Art. 6(1)(c))</p>
                      <p className="text-sm text-slate-500">To comply with applicable transport, employment, and regulatory laws</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-800">Legitimate Interests (Art. 6(1)(f))</p>
                      <p className="text-sm text-slate-500">To protect the safety of users, mitigate fraud, and manage platform risk</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-800">Contractual Necessity (Art. 6(1)(b))</p>
                      <p className="text-sm text-slate-500">To enable Drivers to provide delivery services through the platform</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Section 1: What Documents We Collect */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  What Documents We Collect
                </h2>
                <p className="text-slate-600 mb-4">
                  To verify your eligibility to deliver with Hambre, we require the following documents:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-800">Driving Licence Documentation</p>
                      <p className="text-sm text-slate-500">Valid UK or EU driving licence (front and back)</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-800">Proof of Motor Insurance</p>
                      <p className="text-sm text-slate-500">Valid insurance certificate covering food delivery</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-800">Vehicle Registration Documents (car/scooter only)</p>
                      <p className="text-sm text-slate-500">V5C registration document for motorised vehicles</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-slate-300 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-800">Right to Work Documentation (if applicable)</p>
                      <p className="text-sm text-slate-500">Passport, visa, or share code if applicable</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-slate-300 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-800">Other Legally Required Documentation</p>
                      <p className="text-sm text-slate-500">Any additional documents required by regulatory authorities or insurance providers</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Section 2: How We Handle Your Documents */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Shield className="w-4 h-4 text-purple-600" />
                  </div>
                  How We Handle Your Documents
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">During Verification</h3>
                    <ul className="space-y-2 text-slate-600">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-500 mt-1">•</span>
                        Documents are uploaded securely using encrypted connections (HTTPS/TLS)
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-500 mt-1">•</span>
                        Files are stored temporarily in secure, access-controlled storage
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-500 mt-1">•</span>
                        Only authorised verification staff can access your documents
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-500 mt-1">•</span>
                        Documentation is reviewed for authenticity, validity, and compliance
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-500 mt-1">•</span>
                        Review is typically completed within 24-48 hours
                      </li>
                    </ul>
                  </div>

                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />
                      After Verification
                    </h3>
                    <p className="text-green-700 text-sm mb-2">
                      <strong>Your original document files are permanently deleted immediately after review.</strong>
                    </p>
                    <ul className="space-y-1 text-green-700 text-sm">
                      <li>• Hambre does not retain copies of the original uploaded documents</li>
                      <li>• Raw files (including scans, images, or PDFs) are permanently deleted from active systems</li>
                      <li>• This applies whether your application is approved or rejected</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 3: What Data We Store */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Database className="w-4 h-4 text-orange-600" />
                  </div>
                  Metadata Retained After Verification
                </h2>
                <p className="text-slate-600 mb-4">
                  Following verification, Hambre retains only the following limited verification metadata:
                </p>
                
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-600">Document Type</span>
                    <span className="text-slate-400 text-sm">e.g., Driving Licence, Insurance</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-600">Verification Status</span>
                    <span className="text-slate-400 text-sm">Confirmed/Pending/Rejected</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-600">Expiry Date</span>
                    <span className="text-slate-400 text-sm">e.g., 15/03/2028</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-600">Unique Reference Number</span>
                    <span className="text-slate-400 text-sm">e.g., SMITH123456AB9CD (where necessary)</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-600">Date of Verification</span>
                    <span className="text-slate-400 text-sm">e.g., 12/02/2026</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-slate-600">Internal Audit Reference</span>
                    <span className="text-slate-400 text-sm">For compliance tracking</span>
                  </div>
                </div>

                <p className="text-slate-500 text-sm mt-4">
                  <strong>No full document copies are retained unless legally required.</strong> This data is retained 
                  strictly for compliance, audit, and risk management purposes.
                </p>

                <div className="mt-4 bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <p className="text-blue-800 text-sm">
                    <strong>GDPR Compliance:</strong> This approach ensures compliance with:
                  </p>
                  <ul className="mt-2 space-y-1 text-blue-700 text-sm">
                    <li>• Data minimisation principles (Article 5(1)(c))</li>
                    <li>• Storage limitation principles (Article 5(1)(e))</li>
                    <li>• Integrity and confidentiality requirements (Article 5(1)(f))</li>
                  </ul>
                </div>
              </div>

              {/* Section 4: Retention Period */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-blue-600" />
                  </div>
                  Retention Period
                </h2>
                <p className="text-slate-600 mb-4">
                  Verification metadata is retained only for as long as necessary to:
                </p>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Demonstrate compliance with legal obligations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Satisfy audit or insurance requirements</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Defend potential legal claims</span>
                  </li>
                </ul>
                <p className="text-slate-500 text-sm mt-4">
                  After this period, the data will be securely deleted or anonymised.
                </p>
              </div>

              {/* Section 5: Document Expiry & Renewal */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-amber-600" />
                  </div>
                  Ongoing Obligation & Document Expiry
                </h2>
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 mb-4">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium mb-1">Important</p>
                      <p>
                        Drivers are responsible for ensuring that all documentation remains valid and up to date. 
                        Hambre reserves the right to suspend or terminate access to the platform if documentation expires or is invalid.
                      </p>
                    </div>
                  </div>
                </div>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>We'll email you <strong>30 days before</strong> any document expires</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>You'll receive a secure link to upload renewed documents</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>The same secure handling process applies to renewals</span>
                  </li>
                </ul>
              </div>

              {/* Section 6: Security Measures */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Lock className="w-4 h-4 text-slate-600" />
                  </div>
                  Security Measures
                </h2>
                <p className="text-slate-600 mb-4">
                  Hambre implements appropriate technical and organisational safeguards, including:
                </p>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
                    <span>Encrypted storage and transmission (TLS/SSL)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
                    <span>Restricted access controls</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
                    <span>Secure deletion protocols</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
                    <span>Audit logging</span>
                  </li>
                </ul>
              </div>

              {/* Section 7: Your Rights */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                    <Lock className="w-4 h-4 text-green-600" />
                  </div>
                  Your Rights Under GDPR
                </h2>
                <p className="text-slate-600 mb-4">
                  Under the General Data Protection Regulation (UK GDPR / EU GDPR), you have the following rights:
                </p>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Right to Access:</strong> Request a copy of the data we hold about you</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Right to Rectification:</strong> Request correction of inaccurate data</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Right to Erasure:</strong> Request deletion of your data (subject to legal requirements)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Right to Portability:</strong> Request your data in a portable format</span>
                  </li>
                </ul>
                <p className="text-slate-500 text-sm mt-4">
                  To exercise any of these rights, please contact us at <a href="mailto:privacy@hambre.app" className="text-orange-500 hover:underline">privacy@hambre.app</a>
                </p>
              </div>

              {/* Legal Retention Notice */}
              <div className="bg-slate-100 rounded-2xl p-6">
                <div className="flex gap-3">
                  <AlertCircle className="w-6 h-6 text-slate-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">Document Retention for Legal Purposes</h3>
                    <p className="text-slate-600 text-sm">
                      Hambre reserves the right to retain documentation where required by law, court order, 
                      regulatory investigation, or insurance claim.
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-slate-50 rounded-2xl p-6 text-center">
                <h3 className="font-semibold text-slate-800 mb-2">Questions or Concerns?</h3>
                <p className="text-slate-600 text-sm mb-4">
                  If you have any questions about how we handle your documents, please don't hesitate to contact our Data Protection team.
                </p>
                <a 
                  href="mailto:privacy@hambre.app" 
                  className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-600 font-medium"
                >
                  privacy@hambre.app
                </a>
                <p className="text-slate-400 text-xs mt-4">
                  You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="underline">ico.org.uk</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
